import { EditorContent } from '@tiptap/react';
import { Form, Formik } from 'formik';
import useEventHandler from 'Hooks/useEventHandler';
import { CommentFormat } from 'Generated/schema';
import useRichTextEditorV3, { RichTextEditorStylesV3 } from 'Hooks/useRichTextEditorV3';
import useRouter from 'Hooks/useRouter';
import { Box, Button, Card, Flex, Icon, useSnackbar } from 'pounceV2';
import React from 'react';
import { useTheme } from '@mui/material';
import { AlertActivityHistoryDocument } from '../AlertHistorySection/graphql/alertActivityHistory.generated';
import { useCreateComment } from '../../graphql/createComment.generated';
import RichTextEditorControlsV3 from './RichTextEditorControlsV3';
import { ALERT_ACTIVITY_PAGE_SIZE } from '../AlertHistorySection';

const CommentFormV3 = () => {
  const editor = useRichTextEditorV3({ editable: true });
  const { match } = useRouter<{ id: string }>();
  const alertId = match.params.id;
  const { pushSnackbar } = useSnackbar();
  const theme = useTheme();

  const [createComment, { loading: loadingCreateComment }] = useCreateComment({
    onError: () => {
      pushSnackbar({ variant: 'error', title: 'Something went wrong, your comment was not saved' });
    },
  });

  const handleSubmitComment = useEventHandler(() => {
    return createComment({
      variables: {
        input: {
          body: JSON.stringify(editor.getJSON()),
          alertId,
          format: CommentFormat.JsonSchema,
        },
      },
      refetchQueries: [
        {
          query: AlertActivityHistoryDocument,
          variables: {
            id: alertId,
            activityHistoryPageInfo: { pageSize: ALERT_ACTIVITY_PAGE_SIZE },
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        // we want to clear the editor once this response is successful to give the feeling of the comment "moving" to the new spot visually.
        // we also want to do this only when calls are successful so we don't clear the editor on a failed call and lose all the users comment content
        editor.commands.clearContent();
      },
    });
  });

  const isSubmitDisabled = editor?.getText()?.length === 0 || loadingCreateComment;

  // We are intentionally not using formik here.  This is simply to make the HTML semantically correct
  const initialValues = {};

  return (
    <Flex flexDirection="column" mt={4} mb={8}>
      <Card>
        <RichTextEditorControlsV3 editor={editor} />
        <Box p={4}>
          <Formik initialValues={initialValues} onSubmit={handleSubmitComment}>
            <Form>
              <RichTextEditorStylesV3>
                <EditorContent editor={editor} aria-label="Comment Form" />
              </RichTextEditorStylesV3>
            </Form>
          </Formik>
          <Flex mt={4} justify="space-between" alignItems="flex-end">
            <Icon
              type="markdown"
              size="medium"
              color={theme.palette.tokens.content.assets.semantic.info.base}
              aria-label="Markdown is supported"
            />
            <Button
              variant="secondary"
              size="medium"
              aria-label="Submit Comment"
              type="submit"
              onClick={handleSubmitComment}
              loading={loadingCreateComment}
              disabled={isSubmitDisabled}
            >
              Comment
            </Button>
          </Flex>
        </Box>
      </Card>
    </Flex>
  );
};

export default CommentFormV3;
